// Angular
import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { ActivatedRoute, Router, Params } from '@angular/router';

// Material
import { MatSnackBar } from '@angular/material/snack-bar';

// rxjs
import { Subscription } from 'rxjs';

// localforage
import * as localforage from 'localforage';

// angulartics
import { Angulartics2GoogleAnalytics } from 'angulartics2/ga';
import { Angulartics2 } from 'angulartics2';

// Router animations
import { routerTransition } from '@app/router.animations';

// Services
import { ApiService } from '@app/services/api/api.service';
import { DrawerService } from '@app/services/drawer/drawer.service';
import { ThemeService } from '@app/services/theme/theme.service';

// components
import { AdminSettingsComponent } from '@app/containers/admin-settings/admin-settings.component';
import { OpenHouseSelectComponent } from '@app/containers/open-house-select/open-house-select.component';
import { AdminFeedbackComponent } from '@app/containers/admin-feedback/admin-feedback.component';
import { SurveyThanksComponent } from '@app/containers/survey-thanks/survey-thanks.component';

@Component({
  selector: 'oh-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [routerTransition],
})
export class AppComponent implements OnInit, OnDestroy {
  private routeSubscription: Subscription;
  private routerEventsSubscription: Subscription;
  private agentData: any;
  private token: string;
  public displayAdminButton: boolean;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private swUpdate: SwUpdate,
    private snackbar: MatSnackBar,
    private drawerService: DrawerService,
    private themeService: ThemeService,
    private apiService: ApiService,
    private angulartics2: Angulartics2,
    private angulartics2GoogleAnalytics: Angulartics2GoogleAnalytics,
  ) {
    angulartics2GoogleAnalytics.startTracking();

    // check if service worker update is available
    swUpdate.available.subscribe((event) => {
      console.log('current version is', event.current);
      console.log('available version is', event.available);
      const snackBarRef = snackbar.open('A newer version exists.', 'Update');
      snackBarRef.onAction().subscribe(() => {
        swUpdate.activateUpdate();
        window.location.reload();
      });
    });

    // do stuff when new service worker update is activated
    swUpdate.activated.subscribe((event) => {
      console.log('old version was', event.previous);
      console.log('new version is', event.current);
      snackbar.open('Application version updated.', null, { duration: 3000 });
    });
  }

  async ngOnInit() {
    try {
      this.token = await this.apiService.getTokenFromDatabase();

      if (!this.token) {
        this.activatedRoute.queryParams.subscribe(async params => {
          this.token = params.m;
          if (this.token) {
            await this.autoLogin(this.token);
          }
        });
      }
    } catch (error) {
      console.log(error);
    }

    this.checkStorageUsage();
    this.startWatchingAnalyticsPageViews();
    this.startWatchingAnalyticsEvents();

    this.initAdminButton();

    this.agentData = await this.apiService.getAgentDataFromDatabase();

    if (!this.token || !this.agentData) {
      this.router.navigate(['/login']);
    }

    // handle logic based on route changes
    this.routeSubscription = this.activatedRoute.queryParams.subscribe(
      async (params: Params) => await this.watchRouteForChanges(params),
    );
  }

  ngOnDestroy() {
    this.routeSubscription.unsubscribe();
    this.routerEventsSubscription.unsubscribe();
  }

  private async autoLogin(token: string) { // Allows for auto login via URL
    try {
      this.agentData = await this.apiService.getAgentData(token);
    } catch (error) {
      console.log(error);
    }

    if (this.agentData) {
      try {
        this.apiService.saveTokenToDatabase(token);
        await this.apiService.saveAgentDataToDatabase(this.agentData);
        this.themeService.init(this.agentData);
        this.router.navigate(['/open-house-select']);
      } catch (error) {
        console.log(error);
      }
    }
  }

  private checkStorageUsage() {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      navigator.storage
        .estimate()
        .then(({ usage, quota }) => {
          console.log(`Using ${usage} out of ${quota} bytes.`);
          if (usage >= quota) {
            this.snackbar.open(
              "Oops. Looks like your device doesn't have enough available storage for this app. Please clear up some space and try again.",
              null,
            );
          }
        })
        .catch((error) => {
          console.error('Loading storage estimate failed:');
          console.log(error.stack);
        });
    } else {
      console.error('navigator.storage.estimate API unavailable.');
    }
  }

  private startWatchingAnalyticsPageViews() {
    // keep track off offline events
    this.angulartics2.pageTrack.subscribe(async (event) => {
      if (navigator.onLine === true) {
        return;
      }

      let pageViews: any = await localforage.getItem('oh-analytics-page-views');
      if (!pageViews) {
        pageViews = [];
      }
      pageViews.push(event);
      await localforage.setItem('oh-analytics-page-views', pageViews);
    });
  }

  private startWatchingAnalyticsEvents() {
    // keep track off offline events
    this.angulartics2.eventTrack.subscribe(async (event) => {
      if (navigator.onLine === true) {
        return;
      }

      let events: any = await localforage.getItem('oh-analytics-events');
      if (!events) {
        events = [];
      }
      events.push(event);
      await localforage.setItem('oh-analytics-events', events);
    });
  }

  private async sendAnalytics() {
    const events: any = await localforage.getItem('oh-analytics-events');
    const pageViews: any = await localforage.getItem('oh-analytics-page-views');

    // send events
    if (events && events.length > 0) {
      for (const event of events) {
        this.angulartics2.eventTrack.next(event);
      }
    }

    // send page views
    if (pageViews && pageViews.length > 0) {
      for (const view of pageViews) {
        this.angulartics2.pageTrack.next(view);
      }
    }

    await localforage.removeItem('oh-analytics-events');
    await localforage.removeItem('oh-analytics-page-views');
  }

  private initAdminButton() {
    this.displayAdminButton = !this.router.url.includes('login');
    this.routerEventsSubscription = this.router.events.subscribe((event: any) => {
      if (event && event.url) {
        this.displayAdminButton = !event.url.includes('login');
      }
    });
  }

  @HostListener('window:online', ['$event'])
  @HostListener('window:offline', ['$event'])
  private async onOnlineStatusUpdate(event) {
    if (navigator.onLine) {
      // handle online status
      this.snackbar.open('Online', null, { duration: 2000 });

      try {
        this.token = await this.apiService.getTokenFromDatabase();
      } catch (error) {
        console.log(error);
      }

      if (!this.token) {
        await this.apiService.clearDatabase();
        this.router.navigate(['/login']);
        return;
      }

      await this.postSurveyDataAndClearCache();
      await this.postFeedbackDataAndClearCache();
      await this.sendAnalytics();
    } else {
      // handle offline status
      this.snackbar.open('Offline', null, { duration: 2000 });
    }
  }

  private async watchRouteForChanges(params: Params) {
    // if no token in cache
    if (!this.token) {
      try {
        this.token = await this.apiService.getTokenFromDatabase();
      } catch (error) {
        console.log(error);
      }
    }

    // if still no have token
    if (!this.token) {
      return this.router.navigate(['/login']);
    }

    if (!this.agentData) {
      try {
        this.agentData = await this.apiService.getAgentDataFromDatabase();
      } catch (error) {
        console.log(error);
      }
    }

    try {
      this.themeService.init(this.agentData);
    } catch (error) {
      console.error(error);
    }

    try {
      if (this.token) {
        await this.checkIfOpenHouseSelected(params);
      }
    } catch (error) {
      console.log(error);
    }

    // POST data if online
    if (navigator.onLine && this.token) {
      try {
        await this.postSurveyDataAndClearCache();
        await this.postFeedbackDataAndClearCache();
      } catch (error) {
        console.log(error);
      }
    }

    this.handleDrawerRoutes(params.drawer);
  }

  private async postSurveyDataAndClearCache() {
    let survey: any;

    try {
      survey = await localforage.getItem('oh-survey-submissions');
    } catch (error) {
      console.error(error);
    }

    if (!survey) {
      return;
    }

    for (const entry of survey) {
      try {
        await this.apiService.postSurvey(this.token, entry);
      } catch (error) {
        console.log(error);
      }
    }

    try {
      await localforage.removeItem('oh-survey-submissions');
    } catch (error) {
      console.log(error);
    }
  }

  private async postFeedbackDataAndClearCache() {
    let feedback: any;

    try {
      feedback = await localforage.getItem('oh-feedback-submissions');
    } catch (error) {
      console.error(error);
    }

    if (!feedback) {
      return;
    }

    for (const entry of feedback) {
      try {
        await this.apiService.postFeedback(this.token, entry);
      } catch (error) {
        console.log(error);
      }
    }

    try {
      await localforage.removeItem('oh-feedback-submissions');
    } catch (error) {
      console.log(error);
    }
  }

  private async checkIfOpenHouseSelected(params) {
    let openHouse;

    try {
      openHouse = await localforage.getItem('oh-selected-open-house');
    } catch (error) {
      console.error(error);
    }

    if (!openHouse) {
      if (params && params.drawer === 'admin-settings') {
        this.router.navigate(['open-house-select'], { queryParams: { drawer: 'admin-settings' } });
      } else if (params && params.drawer === 'admin-feedback') {
        this.router.navigate(['open-house-select'], { queryParams: { drawer: 'admin-feedback' } });
      } else {
        this.router.navigate(['open-house-select']);
      }
    }
  }

  private handleDrawerRoutes(key: string) {
    this.drawerService.dismiss();

    // display correct drawer route
    switch (key) {
      case 'admin-feedback':
        this.drawerService.open({ component: AdminFeedbackComponent });
        break;
      case 'admin-settings':
        this.drawerService.open({ component: AdminSettingsComponent });
        break;
      case 'open-house-select':
        this.drawerService.open({ component: OpenHouseSelectComponent, hideCloseButton: true, locked: true });
        break;
      case 'survey-thank-you':
        this.drawerService.open({ component: SurveyThanksComponent, hideCloseButton: true, locked: true });
        break;
      default:
        break;
    }
  }

  public getState(outlet) {
    return outlet.activatedRouteData.state;
  }

  public shouldHideDetails(outlet) {
    const shouldHideDetails = this.getState(outlet) === 'open-house-select' || this.getState(outlet) === 'login' || this.getState(outlet) === 'admin-dashboard';
    return shouldHideDetails;
  }
}
